import { PERMISSION_CATALOG_V2, PermissionKey, hasPermissionV2 } from '@c4ytechnology/authz';
import type {
  AdminConsoleUser,
  AdminIamPermission,
  AdminIamRole,
  AdminIamRoleRef,
} from '../api/adminConsole';

export const ADMIN_IAM_ROLES_QUERY_KEY = ['adminIamRoles'] as const;
export const ADMIN_IAM_PERMISSIONS_QUERY_KEY = ['adminIamPermissions'] as const;
export const adminIamUserAssignmentsQueryKey = (userId: string) => ['adminIamUserAssignments', userId] as const;

export type RolePermissionRow = {
  key: string;
  label: string;
  risk: AdminIamPermission['risk'];
  requiresMfa: boolean;
  /** False when the key is not in the catalog (deprecated or unknown). */
  known: boolean;
};

export type RolePermissionSection = { group: string; items: RolePermissionRow[] };

export const UNKNOWN_PERMISSION_GROUP = 'Other';

/** The shared catalog in the IAM API's shape; used when `GET /admin/iam/permissions` is unavailable. */
export function fallbackPermissionCatalog(): AdminIamPermission[] {
  return PERMISSION_CATALOG_V2.map((d) => ({
    key: d.key,
    label: d.label,
    group: d.group,
    risk: d.risk,
    requiresMfa: d.requiresMfa,
    deprecatedAt: null,
  }));
}

/**
 * Groups a role's permission keys by catalog group, in catalog order. Keys missing from the
 * catalog are kept (never silently dropped) under `Other`, labelled with the raw key.
 */
export function groupRolePermissions(
  rolePermissions: readonly string[],
  catalog: readonly AdminIamPermission[] | null | undefined,
): RolePermissionSection[] {
  const source = catalog && catalog.length > 0 ? catalog : fallbackPermissionCatalog();
  const held = new Set(rolePermissions);
  const sections = new Map<string, RolePermissionRow[]>();
  const seen = new Set<string>();
  for (const item of source) {
    if (!held.has(item.key) || seen.has(item.key)) continue;
    seen.add(item.key);
    const rows = sections.get(item.group) ?? [];
    rows.push({ key: item.key, label: item.label, risk: item.risk, requiresMfa: item.requiresMfa, known: true });
    sections.set(item.group, rows);
  }
  const unknown = [...held].filter((key) => !seen.has(key)).sort();
  if (unknown.length > 0) {
    sections.set(
      UNKNOWN_PERMISSION_GROUP,
      unknown.map((key) => ({ key, label: key, risk: 'critical', requiresMfa: true, known: false })),
    );
  }
  return [...sections.entries()].map(([group, items]) => ({ group, items }));
}

/** System roles first, then by name. */
export function sortRoles(roles: readonly AdminIamRole[]): AdminIamRole[] {
  return [...roles].sort((a, b) => {
    if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
}

/** Roles that can still be added to a user (not already assigned). */
export function assignableRoles(
  roles: readonly AdminIamRole[],
  assigned: readonly Pick<AdminIamRoleRef, 'id'>[],
): AdminIamRole[] {
  const taken = new Set(assigned.map((r) => r.id));
  return sortRoles(roles.filter((r) => !taken.has(r.id)));
}

/**
 * Whether the viewer may add/remove roles on `targetUserId`'s row. Needs `iam.assignments.write`,
 * and never on the viewer's own row (admin-api rejects self-grant too).
 */
export function canManageRoleAssignments(
  viewer: Pick<AdminConsoleUser, 'id' | 'access'> | null | undefined,
  targetUserId: string,
): boolean {
  if (!viewer) return false;
  if (viewer.id === targetUserId) return false;
  return hasPermissionV2(viewer.access, PermissionKey.IAM_ASSIGNMENTS_WRITE);
}

// ── RBAC Phase 2 role editor ──────────────────────────────────────────────────────────────

export const adminIamRoleQueryKey = (roleId: string) => ['adminIamRole', roleId] as const;

export type PermissionPickerSection = { group: string; items: AdminIamPermission[] };

/**
 * Catalog grouped for the role editor's permission picker, in catalog order. Deprecated keys are
 * offered only when the role already holds them (so they can be removed).
 */
export function permissionPickerSections(
  catalog: readonly AdminIamPermission[] | null | undefined,
  rolePermissions: readonly string[] = [],
): PermissionPickerSection[] {
  const source = catalog && catalog.length > 0 ? catalog : fallbackPermissionCatalog();
  const held = new Set(rolePermissions);
  const sections = new Map<string, AdminIamPermission[]>();
  for (const item of source) {
    if (item.deprecatedAt && !held.has(item.key)) continue;
    const rows = sections.get(item.group) ?? [];
    if (!rows.some((r) => r.key === item.key)) rows.push(item);
    sections.set(item.group, rows);
  }
  return [...sections.entries()].map(([group, items]) => ({ group, items }));
}

/**
 * Role edits need the actor to hold every key added *and* removed (Phase 1 rule), so a key the
 * actor does not hold is locked in its current state.
 */
export function permissionLockedForActor(
  actor: Pick<AdminConsoleUser, 'access'> | null | undefined,
  key: string,
): boolean {
  return !(actor?.access?.permissions ?? []).includes(key);
}

/** Local before/after (the server preview adds affected holders and the rule check). */
export function rolePermissionChange(
  before: readonly string[],
  after: readonly string[],
): { added: string[]; removed: string[] } {
  const b = new Set(before);
  const a = new Set(after);
  return {
    added: [...a].filter((k) => !b.has(k)).sort(),
    removed: [...b].filter((k) => !a.has(k)).sort(),
  };
}

export type RoleEditability = {
  canEdit: boolean;
  canDelete: boolean;
  /** Why editing is off, when it is. */
  readOnlyReason?: string;
  /** Why deleting is off, when it is. */
  deleteBlockedReason?: string;
};

/** Super Admin is read-only; system roles are editable but never deletable; writes need iam.roles.write. */
export function roleEditability(
  actor: Pick<AdminConsoleUser, 'access'> | null | undefined,
  role: Pick<AdminIamRole, 'key' | 'isSystem' | 'assignmentCount'>,
): RoleEditability {
  const canWrite = hasPermissionV2(actor?.access, PermissionKey.IAM_ROLES_WRITE);
  if (!canWrite) {
    return {
      canEdit: false,
      canDelete: false,
      readOnlyReason: 'Needs iam.roles.write.',
      deleteBlockedReason: 'Needs iam.roles.write.',
    };
  }
  if (role.key === 'super_admin') {
    return {
      canEdit: false,
      canDelete: false,
      readOnlyReason: 'Super Admin always holds every permission and cannot be edited.',
      deleteBlockedReason: 'System roles cannot be deleted.',
    };
  }
  if (role.isSystem) {
    return { canEdit: true, canDelete: false, deleteBlockedReason: 'System roles cannot be deleted.' };
  }
  if (role.assignmentCount > 0) {
    return {
      canEdit: true,
      canDelete: false,
      deleteBlockedReason: 'Remove every assignment of this role before deleting it.',
    };
  }
  return { canEdit: true, canDelete: true };
}
